import {
  Flex,
  Icons,
} from "@components/index";
import {
  IconName,
  Icons as IconsInternal,
} from "../components/internal/Icons";
import { useCallback } from "react";

const channelInternalIcons: Record<string, IconName> = {
  CEDCMP: "barrel",
  REVENDA: "beer-box",
};

const channelExternalIcons: Record<string, string> = {
  TMS: "truck",
  AS: "shopping-cart",
};

const CHANNEL_LEGENDS = [
  "REVENDA",
  "AS",
  "CED/CMP",
  "TMS",
] as const;

interface GetTypeIconProps {
  channel: string;
  size?: "default" | "large";
}

export function useChannelIcons(event?: Sentinel.Timeline.Event) {
  const getTypeIcon = useCallback(
    ({ channel, size = "default" }: GetTypeIconProps) => {
      const fontSize = size === "large" ? 24 : 14;
      const channelUpper = channel?.toUpperCase().replace('/', "");

      if (channelUpper && channelInternalIcons[channelUpper]) {
        const iconName = channelInternalIcons[channelUpper];
        return (
          <IconsInternal
            name={iconName}
            key={channel}
            width={fontSize}
            height={fontSize}
          />
        );
      }

      if (channelUpper && channelExternalIcons[channelUpper]) {
        return (
          <Icons
            icon={channelExternalIcons[channelUpper]}
            key={channel}
            style={{ fontSize }}
          />
        );
      }

      return null;
    },
    []
  );

  const getChannelIcons = useCallback(() => {
    const channels = event?.channels;
    if (!channels?.length) return null;

    const uniqueChannels = Array.from(new Set(channels));

    return uniqueChannels.map((channel) =>
      getTypeIcon({ channel: channel?.name })
    );
  }, [event?.channels, getTypeIcon]);

  const getChannelIconWithName = useCallback(() => {
    const channels = event?.channels;
    if (!channels?.length) return null;

    const uniqueChannels = Array.from(new Set(channels));

    return (
      <Flex.Col gap="8px">
        {uniqueChannels.map((channel) => {
          const name = channel?.name;
          const iconElement = getTypeIcon({ channel: name, size: "large" });

          if (!iconElement) return null;

          return (
            <Flex.Row key={name} alignItems="center" gap="4px">
              {iconElement}
              <span style={{ fontSize: 14 }}>{name}</span>
            </Flex.Row>
          );
        })}
      </Flex.Col>
    );
  }, [event?.channels, getTypeIcon]);

  const getChannelLegends = useCallback(() => {
    return (
      <Flex.Row gap="16px">
        {CHANNEL_LEGENDS.map((channel) => {
          const iconElement = getTypeIcon({ channel, size: "large" });

          if (!iconElement) return null;

          return (
            <Flex.Row key={channel} alignItems="center" gap="4px">
              {iconElement}
              <span style={{ fontSize: 14 }}>{channel}</span>
            </Flex.Row>
          );
        })}
      </Flex.Row>
    );
  }, [getTypeIcon]);

  const getChannelIconWithNameForFilter = useCallback((channelName: string) => {
    const iconElement = getTypeIcon({ channel: channelName, size: "default" });
    
    return {
      icon: iconElement,
      name: channelName,
    };
  }, [getTypeIcon]);

  return {
    getChannelIcons,
    getTypeIcon,
    getChannelIconWithName,
    getChannelLegends,
    getChannelIconWithNameForFilter,
  };
}
